import _asyncToGenerator from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/vercel/path0/node_modules/@babel/runtime/regenerator/index.js";
import { useRef, useCallback } from 'react';
import useSWR from 'swr';
import { withMiddleware, createCacheHelper, isUndefined, useIsomorphicLayoutEffect, serialize, UNDEFINED, isFunction } from 'swr/_internal';
import { useSyncExternalStore } from 'use-sync-external-store/shim/index.js'; // We have to several type castings here because `useSWRInfinite` is a special

var INFINITE_PREFIX = '$inf$';
var EMPTY_PROMISE = Promise.resolve();

var getFirstPageKey = function getFirstPageKey(getKey) {
  return serialize(getKey ? getKey(0, null) : null)[0];
};

var unstable_serialize = function unstable_serialize(getKey) {
  return INFINITE_PREFIX + getFirstPageKey(getKey);
};

var infinite = function infinite(useSWRNext) {
  return function (getKey, fn, config) {
    var didMountRef = useRef(false);
    var dataRef = useRef();
    var cache = config.cache,
        _config$initialSize = config.initialSize,
        initialSize = _config$initialSize === void 0 ? 1 : _config$initialSize,
        _config$revalidateAll = config.revalidateAll,
        revalidateAll = _config$revalidateAll === void 0 ? false : _config$revalidateAll,
        _config$persistSize = config.persistSize,
        persistSize = _config$persistSize === void 0 ? false : _config$persistSize,
        _config$revalidateFir = config.revalidateFirstPage,
        revalidateFirstPage = _config$revalidateFir === void 0 ? true : _config$revalidateFir,
        _config$revalidateOnM = config.revalidateOnMount,
        revalidateOnMount = _config$revalidateOnM === void 0 ? false : _config$revalidateOnM; // The serialized key of the first page. This key will be used to store
    // metadata of this SWR infinite hook.

    var infiniteKey;

    try {
      infiniteKey = getFirstPageKey(getKey);
      if (infiniteKey) infiniteKey = INFINITE_PREFIX + infiniteKey;
    } catch (err) {// Not ready yet.
    }

    var _createCacheHelper = createCacheHelper(cache, infiniteKey),
        _createCacheHelper2 = _slicedToArray(_createCacheHelper, 3),
        get = _createCacheHelper2[0],
        set = _createCacheHelper2[1],
        subscribeCache = _createCacheHelper2[2];

    var getSnapshot = useCallback(function () {
      var size = isUndefined(get()._l) ? initialSize : get()._l;
      return size; // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cache, infiniteKey, initialSize]);
    useSyncExternalStore(useCallback(function (callback) {
      if (infiniteKey) return subscribeCache(infiniteKey, function () {
        callback();
      });
      return function () {};
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [cache, infiniteKey]), getSnapshot, getSnapshot);
    var resolvePageSize = useCallback(function () {
      var cachedPageSize = get()._l;

      return isUndefined(cachedPageSize) ? initialSize : cachedPageSize; // `cache` isn't allowed to change during the lifecycle
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [infiniteKey, initialSize]); // keep the last page size to restore it with the persistSize option

    var lastPageSizeRef = useRef(resolvePageSize()); // When the page key changes, we reset the page size if it's not persisted

    useIsomorphicLayoutEffect(function () {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }

      if (infiniteKey) {
        // If the key has been changed, we keep the current page size if persistSize is enabled
        set({
          _l: persistSize ? lastPageSizeRef.current : initialSize
        });
      } // `initialSize` isn't allowed to change during the lifecycle
      // eslint-disable-next-line react-hooks/exhaustive-deps

    }, [infiniteKey, cache]); // Needs to check didMountRef during mounting, not in the fetcher

    var shouldRevalidateOnMount = revalidateOnMount && !didMountRef.current; // Actual SWR hook to load all pages in one fetcher.

    var swr = useSWRNext(infiniteKey, /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _ref2, _ref3, forceRevalidateAll, originalData, data, pageSize, previousPageData, i, _serialize, _serialize2, pageKey, pageArg, _createCacheHelper3, _createCacheHelper4, getSWRCache, setSWRCache, pageData, shouldFetchPage;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              // get the revalidate context
              _ref2 = get()._i || [], _ref3 = _slicedToArray(_ref2, 2), forceRevalidateAll = _ref3[0], originalData = _ref3[1]; // return an array of page data

              data = [];
              pageSize = resolvePageSize();
              previousPageData = null;
              i = 0;

            case 5:
              if (!(i < pageSize)) {
                _context.next = 22;
                break;
              }

              _serialize = serialize(getKey(i, previousPageData)), _serialize2 = _slicedToArray(_serialize, 2), pageKey = _serialize2[0], pageArg = _serialize2[1];

              if (pageKey) {
                _context.next = 9;
                break;
              }

              return _context.abrupt("break", 22);

            case 9:
              _createCacheHelper3 = createCacheHelper(cache, pageKey), _createCacheHelper4 = _slicedToArray(_createCacheHelper3, 2), getSWRCache = _createCacheHelper4[0], setSWRCache = _createCacheHelper4[1]; // Get the cached page data.

              pageData = getSWRCache().data; // should fetch (or revalidate) if:
              // - `revalidateAll` is enabled
              // - `mutate()` called
              // - the cache is missing
              // - it's the first page and it's not the initial render
              // - `revalidateOnMount` is enabled and it's on mount
              // - cache for that page has changed

              shouldFetchPage = revalidateAll || forceRevalidateAll || isUndefined(pageData) || revalidateFirstPage && !i && !isUndefined(dataRef.current) || shouldRevalidateOnMount || originalData && !isUndefined(originalData[i]) && !config.compare(originalData[i], pageData);

              if (!(fn && shouldFetchPage)) {
                _context.next = 17;
                break;
              }

              _context.next = 15;
              return fn(pageArg);

            case 15:
              pageData = _context.sent;
              setSWRCache({
                data: pageData,
                _k: pageArg
              });

            case 17:
              data.push(pageData);
              previousPageData = pageData;

            case 19:
              ++i;
              _context.next = 5;
              break;

            case 22:
              // once we executed the data fetching based on the context, clear the context
              set({
                _i: UNDEFINED
              }); // return the data

              return _context.abrupt("return", data);

            case 24:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    })), config); // update dataRef

    useIsomorphicLayoutEffect(function () {
      dataRef.current = swr.data;
    }, [swr.data]);
    var mutate = useCallback( // eslint-disable-next-line func-names
    function (data, opts) {
      // When passing as a boolean, it's explicitly used to disable/enable
      // revalidation.
      var options = typeof opts === 'boolean' ? {
        revalidate: opts
      } : opts || {}; // Default to true.

      var shouldRevalidate = options.revalidate !== false; // It is possible that the key is still falsy.

      if (!infiniteKey) return EMPTY_PROMISE;

      if (shouldRevalidate) {
        if (!isUndefined(data)) {
          // We only revalidate the pages that are changed
          var originalData = dataRef.current;
          set({
            _i: [false, originalData]
          });
        } else {
          // Calling `mutate()`, we revalidate all pages
          set({
            _i: [true]
          });
        }
      }

      return arguments.length ? swr.mutate(data, shouldRevalidate) : swr.mutate();
    }, // swr.mutate is always the same reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infiniteKey, cache]); // Function to load pages data from the cache based on the page size.

    var resolvePagesFromCache = function resolvePagesFromCache(pageSize) {
      // return an array of page data
      var data = [];
      var previousPageData = null;

      for (var i = 0; i < pageSize; ++i) {
        var ref;

        var _serialize3 = serialize(getKey(i, previousPageData)),
            _serialize4 = _slicedToArray(_serialize3, 1),
            pageKey = _serialize4[0]; // Get the cached page data.


        var pageData = pageKey ? (ref = cache.get(pageKey)) == null ? void 0 : ref.data : UNDEFINED; // Return the current data if we can't get it from the cache.

        if (isUndefined(pageData)) return dataRef.current;
        data.push(pageData);
        previousPageData = pageData;
      } // Return the data


      return data;
    }; // Extend the SWR API


    var setSize = useCallback(function (arg) {
      // It is possible that the key is still falsy.
      if (!infiniteKey) return EMPTY_PROMISE;
      var size;

      if (isFunction(arg)) {
        size = arg(resolvePageSize());
      } else if (typeof arg == 'number') {
        size = arg;
      }

      if (typeof size != 'number') return EMPTY_PROMISE;
      set({
        _l: size
      });
      lastPageSizeRef.current = size;
      return mutate(resolvePagesFromCache(size));
    }, // `cache` and `rerender` isn't allowed to change during the lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infiniteKey, resolvePageSize, mutate, cache]); // Use getter functions to avoid unnecessary re-renders caused by triggering
    // all the getters of the returned swr object.

    return {
      size: resolvePageSize(),
      setSize: setSize,
      mutate: mutate,

      get data() {
        return swr.data;
      },

      get error() {
        return swr.error;
      },

      get isValidating() {
        return swr.isValidating;
      },

      get isLoading() {
        return swr.isLoading;
      }

    };
  };
};

var index = withMiddleware(useSWR, infinite);
export { index as default, infinite, unstable_serialize };